import React, { useState, useMemo } from 'react';
import { useData } from '../../context/DataContext';
import { useAuth } from '../../context/AuthContext';
import { 
  RestaurantIcon, 
  DeliveryIcon, 
  ClipboardListIcon,
  StarIcon,
  ClockIcon
} from '../../components/icons';

interface DashboardCardProps {
  title: string;
  count: number;
  icon: any;
  color: string;
  bgColor: string;
  onClick: () => void;
}

const DashboardCard: React.FC<DashboardCardProps> = ({ title, count, icon: Icon, color, bgColor, onClick }) => (
  <button 
    onClick={onClick}
    className={`flex flex-col p-5 rounded-3xl bg-white shadow-sm border border-gray-50 flex-1 hover:shadow-md transition-all active:scale-95 text-left`}
  >
    <div className={`w-12 h-12 rounded-2xl ${bgColor} flex items-center justify-center mb-4`}>
      <Icon className={`w-6 h-6 ${color}`} />
    </div>
    <span className="text-gray-500 font-bold text-xs uppercase tracking-wider">{title}</span>
    <span className="text-2xl font-black text-gray-900 mt-1">{count}</span>
  </button>
);

const SuperAdminDashboard: React.FC<{ onNavigate: (tab: any) => void }> = ({ onNavigate }) => {
    const { logout } = useAuth();
    const { 
        restaurants, 
        allLivreurs,
        allPayments,
    } = useData();

    const pendingRestaurants = useMemo(() => restaurants.filter(r => r.status === 'pending'), [restaurants]);
    const pendingLivreurs = useMemo(() => allLivreurs.filter(l => l.livreurStatus === 'pending'), [allLivreurs]);
    const pendingPayments = useMemo(() => allPayments.filter(p => p.status === 'pending'), [allPayments]);

    const activeRestaurants = useMemo(() => restaurants.filter(r => r.status === 'approved'), [restaurants]);
    const activeLivreurs = useMemo(() => allLivreurs.filter(l => l.livreurStatus === 'approved'), [allLivreurs]);

    return (
        <div className="min-h-screen bg-gray-50 p-6 pb-24">
            <header className="flex justify-between items-center mb-8">
                <div>
                    <h1 className="text-2xl font-black text-gray-900">Yumii Admin 👑</h1>
                    <p className="text-gray-500 text-sm font-medium">Tableau de bord général</p>
                </div>
                <button 
                  onClick={logout} 
                  className="bg-white p-3 rounded-2xl shadow-sm text-red-600 hover:bg-red-50 transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                </button>
            </header>

            <section className="mb-8">
              <h2 className="text-lg font-black text-gray-900 mb-4 flex items-center gap-2">
                <ClockIcon className="w-5 h-5 text-orange-500" />
                En attente de validation
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <DashboardCard 
                    title="Restaurants" 
                    count={pendingRestaurants.length} 
                    icon={RestaurantIcon}
                    color="text-orange-600"
                    bgColor="bg-orange-100"
                    onClick={() => onNavigate('restaurants')}
                  />
                  <DashboardCard 
                    title="Livreurs" 
                    count={pendingLivreurs.length} 
                    icon={DeliveryIcon}
                    color="text-blue-600"
                    bgColor="bg-blue-100"
                    onClick={() => onNavigate('drivers')}
                  />
                  <DashboardCard 
                    title="Paiements" 
                    count={pendingPayments.length} 
                    icon={ClipboardListIcon}
                    color="text-purple-600"
                    bgColor="bg-purple-100"
                    onClick={() => onNavigate('payments')}
                  />
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-lg font-black text-gray-900 mb-4 flex items-center gap-2">
                <StarIcon className="w-5 h-5 text-green-500" />
                Actifs sur la plateforme
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-2 gap-4">
                  <div className="bg-white p-5 rounded-3xl shadow-sm border border-gray-50 text-left">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-gray-500 font-bold text-xs uppercase tracking-wider">Restaurants</span>
                      <RestaurantIcon className="w-5 h-5 text-green-600" />
                    </div>
                    <span className="text-3xl font-black text-gray-900">{activeRestaurants.length}</span>
                  </div>
                  <div className="bg-white p-5 rounded-3xl shadow-sm border border-gray-50 text-left">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-gray-500 font-bold text-xs uppercase tracking-wider">Livreurs</span>
                      <DeliveryIcon className="w-5 h-5 text-blue-600" />
                    </div>
                    <span className="text-3xl font-black text-gray-900">{activeLivreurs.length}</span>
                  </div>
              </div>
            </section>

            <div className="bg-red-600 p-6 rounded-3xl shadow-xl shadow-red-200 text-white relative overflow-hidden">
                <div className="relative z-10 text-left">
                  <h3 className="text-xl font-bold mb-1">Besoin d'aide ?</h3>
                  <p className="text-red-100 text-sm mb-4">Consultez les guides de gestion ou contactez le support technique.</p>
                  <button className="bg-white text-red-600 px-6 py-2 rounded-xl font-bold text-sm shadow-lg active:scale-95 transition-all">
                    Guide Admin
                  </button>
                </div>
                <div className="absolute right-[-20px] bottom-[-20px] opacity-10 rotate-12">
                   <RestaurantIcon className="w-32 h-32" />
                </div>
            </div>
        </div>
    );
};

export default SuperAdminDashboard;